import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Autocomplete,
  Checkbox,
  ListItemIcon
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  AddCircle as AddCircleIcon,
  Edit as EditOptionIcon,
  Delete as DeleteOptionIcon,
  Public as GlobalIcon,
  Business as ProductIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import attributeService from '../services/attributeService';
import { productService } from '../services/productService';

const Attributes = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [openOptionDialog, setOpenOptionDialog] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [expandedAttribute, setExpandedAttribute] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [error, setError] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    inputType: 'select',
    isFixed: false,
    isRequired: false,
    description: '',
    validationRules: {},
    applicableProducts: [],
    isGlobal: false
  });

  const [optionData, setOptionData] = useState({
    value: '',
    displayValue: '',
    metadata: {}
  });

  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [attributeOptions, setAttributeOptions] = useState([]);

  // Predefined attribute names
  const predefinedAttributeNames = [
    'Pairs',
    'Pack',
    'Color',
    'Colors',
    'Type',
    'Length',
    'Material',
    'Pieces',
    'Sets'
  ];

  const inputTypes = [
    { value: 'select', label: 'Single Select' },
    { value: 'multi-select', label: 'Multi Select' },
    { value: 'text', label: 'Text Input' },
    { value: 'number', label: 'Number Input' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'color-picker', label: 'Color Picker' },
  ];

  const fetchAttributes = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        search: searchTerm,
        inputType: filterType,
        sortBy,
        sortOrder
      };
      const response = await attributeService.getAllAttributes(params);
      setAttributes(response.attributes || []);
    } catch (error) {
      setError(error.message || 'Failed to fetch attributes');
      toast.error('Failed to fetch attributes');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterType, sortBy, sortOrder]);

  useEffect(() => {
    fetchAttributes();
    fetchProducts();
  }, [fetchAttributes]);

  const fetchProducts = async () => {
    try {
      const response = await productService.getAllProducts();
      setProducts(response.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const refreshAttributeOptions = useCallback(async (attributeId) => {
    if (!attributeId) return;
    try {
      const response = await attributeService.getAttributeById(attributeId);
      const updatedAttribute = response?.attribute || response;
      const updatedOptions = updatedAttribute?.options || [];
      setAttributeOptions(updatedOptions);
      setEditingAttribute((prev) => {
        if (prev && prev._id === attributeId) {
          return { ...prev, options: updatedOptions };
        }
        return prev;
      });
    } catch (error) {
      console.error('Failed to refresh attribute options:', error);
    }
  }, []);

  const addOption = () => {
    if (newOption.trim()) {
      const option = {
        value: newOption.trim(),
        displayValue: newOption.trim(),
      };
      setAttributeOptions([...attributeOptions, option]);
      setNewOption('');
    }
  };

  const removeOption = (index) => {
    const updatedOptions = attributeOptions.filter((_, i) => i !== index);
    setAttributeOptions(updatedOptions);
  };

  const handleCreateAttribute = async () => {
    try {
      setLoading(true);
      const attributeData = { ...formData };
      
      // If it's a select or multi-select type, create the attribute first then add options
      if (formData.inputType === 'select' || formData.inputType === 'multi-select') {
        const response = await attributeService.createAttribute(attributeData);
        const attributeId = response.attribute._id;
        
        // Add options if any were created
        if (attributeOptions.length > 0) {
          for (const option of attributeOptions) {
            await attributeService.addAttributeOption(attributeId, option);
          }
        }
      } else {
        await attributeService.createAttribute(attributeData);
      }
      
      toast.success('Attribute created successfully');
      setOpenDialog(false);
      resetForm();
      fetchAttributes();
    } catch (error) {
      toast.error(error.message || 'Failed to create attribute');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAttribute = async () => {
    try {
      setLoading(true);
      const attributeData = { ...formData };
      
      // Update the attribute
      await attributeService.updateAttribute(editingAttribute._id, attributeData);
      
      // If it's a select or multi-select type, handle options
      if (formData.inputType === 'select' || formData.inputType === 'multi-select') {
        // For now, we'll just update the attribute itself
        // Options management should be done separately through the options dialog
        console.log('Options management for edit will be handled separately');
      }
      
      toast.success('Attribute updated successfully');
      setOpenDialog(false);
      resetForm();
      fetchAttributes();
    } catch (error) {
      toast.error(error.message || 'Failed to update attribute');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAttribute = async (id) => {
    if (window.confirm('Are you sure you want to delete this attribute? This will also delete all associated options.')) {
      try {
        setLoading(true);
        await attributeService.deleteAttribute(id);
        toast.success('Attribute deleted successfully');
        fetchAttributes();
      } catch (error) {
        toast.error(error.message || 'Failed to delete attribute');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddOption = async () => {
    if (!selectedAttribute?._id) {
      toast.error('No attribute selected for this option');
      return;
    }
    try {
      setLoading(true);
      await attributeService.addAttributeOption(selectedAttribute._id, optionData);
      toast.success('Option added successfully');
      await refreshAttributeOptions(selectedAttribute._id);
      setOpenOptionDialog(false);
      resetOptionForm();
      fetchAttributes();
    } catch (error) {
      toast.error(error.message || 'Failed to add option');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOption = async () => {
    if (!selectedAttribute?._id || !editingOption?._id) {
      toast.error('No option selected for update');
      return;
    }
    try {
      setLoading(true);
      await attributeService.updateAttributeOption(editingOption._id, optionData);
      toast.success('Option updated successfully');
      await refreshAttributeOptions(selectedAttribute._id);
      setOpenOptionDialog(false);
      resetOptionForm();
      fetchAttributes();
    } catch (error) {
      toast.error(error.message || 'Failed to update option');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOption = async (attributeId, optionId) => {
    if (!optionId) {
      toast.error('Invalid option selected for deletion');
      return;
    }
    if (window.confirm('Are you sure you want to delete this option?')) {
      try {
        setLoading(true);
        await attributeService.deleteAttributeOption(optionId);
        toast.success('Option deleted successfully');
        if (attributeId) {
          await refreshAttributeOptions(attributeId);
        }
        fetchAttributes();
      } catch (error) {
        toast.error(error.message || 'Failed to delete option');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      inputType: 'select',
      isFixed: false,
      isRequired: false,
      description: '',
      validationRules: {},
      applicableProducts: [],
      isGlobal: false
    });
    setSelectedProducts([]);
    setAttributeOptions([]);
    setNewOption('');
    setEditingAttribute(null);
  };

  const resetOptionForm = () => {
    setOptionData({
      value: '',
      displayValue: '',
      metadata: {}
    });
    setEditingOption(null);
    setSelectedAttribute(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setOpenDialog(true);
  };

  const openEditDialog = (attribute) => {
    setFormData({
      name: attribute.name,
      inputType: attribute.inputType,
      isFixed: attribute.isFixed,
      isRequired: attribute.isRequired,
      description: attribute.description || '',
      validationRules: attribute.validationRules || {},
      applicableProducts: attribute.applicableProducts || [],
      isGlobal: attribute.isGlobal || false
    });
    
    // Set selected products for the autocomplete
    const applicableProducts = attribute.applicableProducts || [];
    const selectedProductsData = products.filter(product => 
      applicableProducts.some(ap => ap._id === product._id || ap === product._id)
    );
    setSelectedProducts(selectedProductsData);
    
    // Set existing options for the attribute
    setAttributeOptions(attribute.options || []);
    
    setEditingAttribute(attribute);
    setOpenDialog(true);
  };

  const openAddOptionDialog = (attribute, option = null) => {
    setSelectedAttribute(attribute);
    if (option) {
      setOptionData({
        value: option.value,
        displayValue: option.displayValue || '',
        metadata: option.metadata || {}
      });
      setEditingOption(option);
    } else {
      setOptionData({
        value: '',
        displayValue: '',
        metadata: {}
      });
      setEditingOption(null);
    }
    setOpenOptionDialog(true);
  };

  const getInputTypeLabel = (type) => {
    const inputType = inputTypes.find(t => t.value === type);
    return inputType ? inputType.label : type;
  };

  const getInputTypeColor = (type) => {
    const colors = {
      'select': 'primary',
      'multi-select': 'secondary',
      'text': 'success',
      'number': 'warning',
      'boolean': 'error',
      'color-picker': 'info',
    };
    return colors[type] || 'default';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Attribute Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ bgcolor: 'primary.main' }}
        >
          Create Attribute
        </Button>
      </Box>

      {/* Search and Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Search attributes"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or description"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Input Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Input Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {inputTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="inputType">Input Type</MenuItem>
                  <MenuItem value="createdAt">Created Date</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Attributes List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {attributes.map((attribute) => (
            <Accordion
              key={attribute._id}
              expanded={expandedAttribute === attribute._id}
              onChange={() => setExpandedAttribute(expandedAttribute === attribute._id ? null : attribute._id)}
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{attribute.name}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        label={getInputTypeLabel(attribute.inputType)}
                        color={getInputTypeColor(attribute.inputType)}
                        size="small"
                      />
                      {attribute.isFixed && (
                        <Chip label="Fixed" color="warning" size="small" />
                      )}
                      {attribute.isRequired && (
                        <Chip label="Required" color="error" size="small" />
                      )}
                      {attribute.isGlobal ? (
                        <Chip 
                          label="Global" 
                          color="info" 
                          size="small" 
                          icon={<GlobalIcon />}
                        />
                      ) : (
                        <Chip 
                          label={`${attribute.applicableProducts?.length || 0} Products`} 
                          color="secondary" 
                          size="small" 
                          icon={<ProductIcon />}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {/* <Tooltip title="Add Option">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openAddOptionDialog(attribute);
                        }}
                        size="small"
                      >
                        <AddCircleIcon />
                      </IconButton>
                    </Tooltip> */}
                    <Tooltip title="Edit Attribute">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(attribute);
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Attribute">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAttribute(attribute._id);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {attribute.description || 'No description provided'}
                </Typography>
                
                {/* Options List */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Options ({attribute.options?.length || 0})
                </Typography>
                {attribute.options && attribute.options.length > 0 ? (
                  <List dense>
                    {attribute.options.map((option) => (
                      <ListItem key={option._id} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={option.displayValue || option.value}
                          secondary={option.value !== option.displayValue ? option.value : null}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            onClick={() => openAddOptionDialog(attribute, option)}
                            size="small"
                          >
                            <EditOptionIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteOption(attribute._id, option._id)}
                            size="small"
                            color="error"
                          >
                            <DeleteOptionIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No options available. Click the + button to add options.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Create/Edit Attribute Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingAttribute ? 'Edit Attribute' : 'Create New Attribute'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                freeSolo
                options={predefinedAttributeNames}
                value={formData.name}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'string') {
                    setFormData({ ...formData, name: newValue });
                  } else if (newValue && newValue.inputValue) {
                    setFormData({ ...formData, name: newValue.inputValue });
                  } else if (newValue) {
                    setFormData({ ...formData, name: newValue });
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, name: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Attribute Name"
                    required
                    helperText="Select from predefined names or type a custom name"
                  />
                )}
                renderOption={(props, option) => {
                  const isCustom = !predefinedAttributeNames.includes(option);
                  return (
                    <li {...props}>
                      {isCustom && option.inputValue ? `Add "${option.inputValue}"` : option}
                    </li>
                  );
                }}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option;
                  }
                  if (option.inputValue) {
                    return option.inputValue;
                  }
                  return option;
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter((option) =>
                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  
                  // If the input doesn't match any predefined option, suggest creating a new one
                  if (params.inputValue && !filtered.some(option => option.toLowerCase() === params.inputValue.toLowerCase())) {
                    filtered.push({
                      inputValue: params.inputValue,
                      title: `Add "${params.inputValue}"`,
                    });
                  }
                  
                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Input Type</InputLabel>
                <Select
                  value={formData.inputType}
                  onChange={(e) => setFormData({ ...formData, inputType: e.target.value })}
                  label="Input Type"
                >
                  {inputTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isFixed}
                      onChange={(e) => setFormData({ ...formData, isFixed: e.target.checked })}
                    />
                  }
                  label="Fixed Attribute"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isRequired}
                      onChange={(e) => setFormData({ ...formData, isRequired: e.target.checked })}
                    />
                  }
                  label="Required"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isGlobal}
                      onChange={(e) => {
                        setFormData({ ...formData, isGlobal: e.target.checked, applicableProducts: [] });
                        if (e.target.checked) {
                          setSelectedProducts([]);
                        }
                      }}
                    />
                  }
                  label="Global Attribute"
                />
              </Box>
            </Grid>
            
            {!formData.isGlobal && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={products}
                  getOptionLabel={(option) => option.title}
                  value={selectedProducts}
                  onChange={(event, newValue) => {
                    setSelectedProducts(newValue);
                    setFormData({ ...formData, applicableProducts: newValue.map(p => p._id) });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Applicable Products"
                      placeholder="Select products for this attribute"
                    />
                  )}
                  renderOption={(props, option, { selected }) => (
                    <ListItem {...props}>
                      <ListItemIcon>
                        <Checkbox checked={selected} />
                      </ListItemIcon>
                      <ListItemText primary={option.title} />
                    </ListItem>
                  )}
                />
              </Grid>
            )}

            {/* Options Management for Select and Multi-Select */}
            {(formData.inputType === 'select' || formData.inputType === 'multi-select') && (
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                  Attribute Options
                </Typography>

                {editingAttribute ? (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Manage options for this attribute directly.
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => openAddOptionDialog(editingAttribute)}
                        startIcon={<AddCircleIcon />}
                      >
                        Add Option
                      </Button>
                    </Box>

                    {attributeOptions.length > 0 ? (
                      <List dense>
                        {attributeOptions.map((option) => (
                          <ListItem key={option._id || option.value} sx={{ py: 0.5 }}>
                            <ListItemText
                              primary={option.displayValue || option.value}
                              secondary={
                                option.displayValue && option.displayValue !== option.value
                                  ? option.value
                                  : null
                              }
                            />
                            <ListItemSecondaryAction>
                              {option._id && (
                                <Tooltip title="Edit Option">
                                  <IconButton
                                    onClick={() => openAddOptionDialog(editingAttribute, option)}
                                    size="small"
                                  >
                                    <EditOptionIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {option._id && (
                                <Tooltip title="Delete Option">
                                  <IconButton
                                    onClick={() => handleDeleteOption(editingAttribute._id, option._id)}
                                    size="small"
                                    color="error"
                                  >
                                    <DeleteOptionIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {'No options available. Click "Add Option" to create one.'}
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <TextField
                        fullWidth
                        label="Add Option"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addOption();
                          }
                        }}
                        placeholder="Enter option value"
                      />
                      <Button
                        variant="outlined"
                        onClick={addOption}
                        disabled={!newOption.trim()}
                        startIcon={<AddCircleIcon />}
                      >
                        Add
                      </Button>
                    </Box>

                    {attributeOptions.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Options ({attributeOptions.length})
                        </Typography>
                        <List dense>
                          {attributeOptions.map((option, index) => (
                            <ListItem key={index} sx={{ py: 0.5 }}>
                              <ListItemText primary={option.value} />
                              <ListItemSecondaryAction>
                                <IconButton
                                  onClick={() => removeOption(index)}
                                  size="small"
                                  color="error"
                                >
                                  <DeleteOptionIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={editingAttribute ? handleUpdateAttribute : handleCreateAttribute}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingAttribute ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Option Dialog */}
      <Dialog open={openOptionDialog} onClose={() => setOpenOptionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingOption ? 'Edit Option' : 'Add New Option'}
          {selectedAttribute && (
            <Typography variant="body2" color="text.secondary">
              for {selectedAttribute.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Option Value"
                value={optionData.value}
                onChange={(e) => setOptionData({ ...optionData, value: e.target.value })}
                required
                helperText="This is the actual value stored in the database"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Value"
                value={optionData.displayValue}
                onChange={(e) => setOptionData({ ...optionData, displayValue: e.target.value })}
                helperText="This is what users will see (optional, defaults to value)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOptionDialog(false)}>Cancel</Button>
          <Button
            onClick={editingOption ? handleUpdateOption : handleAddOption}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : (editingOption ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Attributes;
